import { MAJOR_ARCANA } from '@lunary/tarot-data'

export const CARD_WIDTH = 180
export const CARD_HEIGHT = 314
export const CARD_RATIO = CARD_HEIGHT / CARD_WIDTH

export type DeckView = {
  readonly id: string
  readonly back: string | null
  readonly backs: readonly string[]
  readonly faces: Readonly<Record<string, string>>
  readonly pickFaces: Readonly<Record<string, string>>
}

export type FaceCard = { readonly slug: string; readonly number: number; readonly arcana: string }

type PublicImage = { url?: unknown; medium_url?: unknown }
type PublicCard = PublicImage & { card_id?: unknown }
type PublicBody = { decks?: unknown; backs?: unknown }
type PublicDeck = { id?: unknown; kind?: unknown; back?: PublicImage | null; cards?: unknown }

const DECK_FILE_RE = /(\/decks\/files\/[0-9a-f]{32})(?:\.[sm])?\.webp$/
const THUMB_MAX_WIDTH = 120

function imageUrl(image: PublicImage | null | undefined): string | null {
  if (typeof image?.medium_url === 'string') return image.medium_url
  return typeof image?.url === 'string' ? image.url : null
}

export function sizedDeckImage(src: string, cssWidth: number): string {
  return cssWidth <= THUMB_MAX_WIDTH ? src.replace(DECK_FILE_RE, '$1.s.webp') : src
}

function stringMap(raw: unknown): Record<string, string> {
  const parsed: Record<string, string> = {}
  if (typeof raw !== 'object' || raw === null) return parsed
  for (const [key, value] of Object.entries(raw)) if (typeof value === 'string') parsed[key] = value
  return parsed
}

function facesOf(deck: PublicDeck | undefined, resolve: (path: string) => string): Record<string, string> {
  const faces: Record<string, string> = {}
  if (!deck || !Array.isArray(deck.cards)) return faces
  for (const card of deck.cards as PublicCard[]) {
    const url = imageUrl(card)
    if (typeof card.card_id === 'string' && url) faces[card.card_id] = resolve(url)
  }
  return faces
}

export function parseDeck(raw: unknown): DeckView | null {
  if (typeof raw !== 'object' || raw === null) return null
  const { id, back, backs, faces, pickFaces } = raw as { id?: unknown; back?: unknown; backs?: unknown; faces?: unknown; pickFaces?: unknown }
  if (typeof id !== 'string' || typeof faces !== 'object' || faces === null) return null
  const list = Array.isArray(backs) ? backs.filter((b): b is string => typeof b === 'string') : []
  return { id, back: typeof back === 'string' ? back : null, backs: list, faces: stringMap(faces), pickFaces: stringMap(pickFaces) }
}

export function deckFromResponse(body: unknown, resolve: (path: string) => string): DeckView | null {
  const parsed: PublicBody = typeof body === 'object' && body !== null ? body : {}
  const list: PublicDeck[] = Array.isArray(parsed.decks) ? (parsed.decks as PublicDeck[]) : []
  const daily = list.find((d) => d.kind === 'daily_card') ?? list.find((d) => d.kind === undefined)
  const three = list.find((d) => d.kind === 'three_cards')
  const first = daily ?? three
  if (!first || typeof first.id !== 'string' || !Array.isArray(first.cards)) return null
  const faces = facesOf(first, resolve)
  const pickFaces = facesOf(three, resolve)
  const backUrl = imageUrl(first.back)
  const back = backUrl ? resolve(backUrl) : null
  const library = Array.isArray(parsed.backs)
    ? (parsed.backs as PublicImage[]).flatMap((b) => {
        const url = imageUrl(b)
        return url ? [resolve(url)] : []
      })
    : []
  const id = three && typeof three.id === 'string' && three !== first ? `${first.id}+${three.id}` : first.id
  return { id, back, backs: library.length > 0 ? library : back ? [back] : [], faces, pickFaces }
}

export function deckFaceUrl(deck: DeckView | null, number: number): string | null {
  const slug = MAJOR_ARCANA[number]?.id
  return (slug ? deck?.faces[slug] : undefined) ?? null
}

export function deckFaceBySlug(deck: DeckView | null, slug: string): string | null {
  return deck?.faces[slug] ?? null
}

export function pickFaceUrl(deck: DeckView | null, slug: string): string | null {
  return deck?.pickFaces[slug] ?? deck?.faces[slug] ?? null
}
